import * as XLSX from "xlsx";

// Shared palette for the BI & Reporting surface.
export const C = {
  ink: "#10254A", slate: "#33415C", muted: "#6A778E", line: "#E6EBF3", ground: "#F5F7FB",
  blue: "#2F6BE0", green: "#3FA13B", amber: "#E7A13C", red: "#E24B4A", violet: "#7A5AF8",
};

// Risk bands on the 1-10 ARC scale (10 = worst).
export const bandColor = (s) => (s == null ? C.muted : s <= 3.5 ? C.green : s <= 6.5 ? C.amber : C.red);
export const bandLabel = (s) => (s == null ? "Unscored" : s <= 3.5 ? "Low (1–3.5)" : s <= 6.5 ? "Moderate (3.6–6.5)" : "High (6.6–10)");

export const toNum = (v) => {
  if (v == null) return 0;
  const n = parseFloat(String(v).replace(/[^0-9.\-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};
export const fmtUSD = (n) => {
  if (!n) return "$0";
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${Math.round(n).toLocaleString()}`;
};
export const fmtFull = (n) => `$${Math.round(n || 0).toLocaleString()}`;

const REGION_BY_COUNTRY = {
  mexico: "North America", "united states": "North America", canada: "North America",
  guatemala: "Central America", honduras: "Central America", nicaragua: "Central America",
  "costa rica": "Central America", panama: "Central America", "el salvador": "Central America",
  brazil: "South America", argentina: "South America", colombia: "South America",
  peru: "South America", chile: "South America", ecuador: "South America", paraguay: "South America",
  uruguay: "South America", bolivia: "South America", "dominican republic": "Caribbean",
  malaysia: "Asia", vietnam: "Asia", india: "Asia", singapore: "Asia", indonesia: "Asia",
  egypt: "Africa", nigeria: "Africa", rwanda: "Africa", morocco: "Africa", turkey: "Middle East",
};
export const regionOf = (country) => {
  if (!country) return "Other";
  return REGION_BY_COUNTRY[String(country).toLowerCase().trim()] || "Other";
};

export const csvEscape = (v) => {
  const s = v == null ? "" : String(v);
  return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
};

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
};

// data: array of plain objects. headers: [{ key, label }].
export const exportCSV = (data, headers, filename) => {
  const head = headers.map((h) => csvEscape(h.label)).join(",");
  const lines = data.map((row) => headers.map((h) => csvEscape(row[h.key])).join(","));
  const csv = "﻿" + [head, ...lines].join("\r\n");
  triggerDownload(new Blob([csv], { type: "text/csv;charset=utf-8;" }), `${filename}.csv`);
};

export const exportExcel = (data, headers, filename, sheetName = "Report") => {
  const rows = data.map((row) => {
    const o = {};
    headers.forEach((h) => { o[h.label] = row[h.key]; });
    return o;
  });
  const ws = XLSX.utils.json_to_sheet(rows, { header: headers.map((h) => h.label) });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

// PDF via a scoped print window (no extra deps) — renders a clean branded table and prints.
export const exportPDF = (data, headers, filename, title, subtitle) => {
  const esc = (s) => String(s == null ? "" : s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  const thead = headers.map((h) => `<th>${esc(h.label)}</th>`).join("");
  const tbody = data.map((row) => `<tr>${headers.map((h) => `<td>${esc(row[h.key])}</td>`).join("")}</tr>`).join("");
  const w = window.open("", "_blank", "width=1000,height=800");
  if (!w) return;
  w.document.write(`<!doctype html><html><head><title>${esc(filename)}</title><meta charset="utf-8"/>
    <style>
      *{box-sizing:border-box;} body{font-family:Segoe UI,Helvetica,Arial,sans-serif;color:#33415C;margin:28px;}
      .bar{background:#10254A;color:#fff;border-radius:12px;padding:18px 22px;margin-bottom:18px;}
      .bar .brand{font-size:18px;font-weight:700;letter-spacing:-.01em;}
      .bar h1{font-size:20px;margin:8px 0 2px;font-weight:700;}
      .bar p{margin:0;color:#C4D2EA;font-size:13px;}
      table{width:100%;border-collapse:collapse;font-size:12px;}
      th{background:#F0F2F7;color:#10254A;text-align:left;padding:8px 10px;text-transform:uppercase;font-size:10px;letter-spacing:.05em;border-bottom:1px solid #E6EBF3;}
      td{padding:7px 10px;border-bottom:1px solid #EEF1F7;}
      tr:nth-child(even) td{background:#FAFBFE;}
      .meta{margin-top:14px;font-size:11px;color:#6A778E;}
      @media print{.noprint{display:none;}}
    </style></head><body>
    <div class="bar"><div class="brand">CrediArc</div><h1>${esc(title || "Portfolio Report")}</h1><p>${esc(subtitle || "")}</p></div>
    <table><thead><tr>${thead}</tr></thead><tbody>${tbody}</tbody></table>
    <div class="meta">${data.length} rows · generated ${new Date().toLocaleString()}</div>
    <script>window.onload=function(){window.print();}</script>
    </body></html>`);
  w.document.close();
};
